import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GAS_UNITS, fmtGas, gasPriceUnit, toGasPrice, toGasUnit } from "./eaFormat";

const STORE_KEY = "ea_filters_v1";
const UNIT_KEY = "ea_power_unit";
const GAS_UNIT_KEY = "ea_gas_unit";
const FILTER_KEYS = ["company", "year", "points", "month", "day"];
const PERSISTED = ["company", "year", "points"];
const RESTORED = ["company", "points"]; // l'anno non si ripropone: all'apertura vale l'ultimo anno con dati

export const EaContext = createContext(null);
export const useEa = () => useContext(EaContext);

function readStore() {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || "{}"); } catch { return {}; }
}

// Filtri globali: indirizzo della pagina come fonte, browser come memoria.
export function useEaFilterState() {
  const [params, setParams] = useSearchParams();
  const [unit, setUnitState] = useState(() => localStorage.getItem(UNIT_KEY) || "MWh");
  const [gasUnit, setGasUnitState] = useState(() => localStorage.getItem(GAS_UNIT_KEY) || "Smc");

  useEffect(() => {
    const stored = readStore();
    const missing = RESTORED.filter((k) => !params.get(k) && stored[k]);
    if (missing.length) {
      const next = new URLSearchParams(params);
      missing.forEach((k) => next.set(k, stored[k]));
      setParams(next, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filters = useMemo(() => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) || ""])), [params]);

  const setFilter = useCallback((patch) => {
    const next = new URLSearchParams(window.location.search);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, String(v)) : next.delete(k)));
    setParams(next, { replace: true });
    const stored = readStore();
    Object.entries(patch).forEach(([k, v]) => {
      if (!PERSISTED.includes(k)) return;
      if (v) stored[k] = String(v); else delete stored[k];
    });
    localStorage.setItem(STORE_KEY, JSON.stringify(stored));
  }, [setParams]);

  const setUnit = useCallback((u) => { setUnitState(u); localStorage.setItem(UNIT_KEY, u); }, []);
  const setGasUnit = useCallback((u) => { setGasUnitState(u); localStorage.setItem(GAS_UNIT_KEY, u); }, []);

  const search = useMemo(() => {
    const s = new URLSearchParams();
    PERSISTED.forEach((k) => filters[k] && s.set(k, filters[k]));
    const str = s.toString();
    return str ? `?${str}` : "";
  }, [filters]);

  return { filters, setFilter, unit, setUnit, gasUnit: GAS_UNITS[gasUnit] ? gasUnit : "Smc", setGasUnit, search };
}

export function eaParams(filters, extra = {}) {
  const p = {};
  if (filters.company) p.company_id = filters.company;
  if (filters.year) p.year = filters.year;
  Object.entries(extra).forEach(([k, v]) => { if (v !== "" && v != null) p[k] = v; });
  return p;
}

// Unità gas scelta: q = quantità da Smc, f = formattata, p/pu = prezzo e sua unità (da c€/Smc o unità del file).
export function useGasUnit() {
  const { gasUnit: u, setGasUnit } = useContext(EaContext);
  return { u, setU: setGasUnit, d: GAS_UNITS[u].digits, q: (v) => toGasUnit(v, u), f: (v) => fmtGas(v, u), p: (v, src) => toGasPrice(v, u, src), pu: (src) => gasPriceUnit(u, src) };
}
